// Watches the producer's own device location via the browser Geolocation
// API and returns it as [lng, lat] (or null until a fix arrives / when
// unsupported or denied). Shared by the Live Tracking view and the zone
// setup map so both follow the same watch settings and cleanup. Errors
// are swallowed on purpose: a missing location just leaves the dot off
// the map — it must never break map rendering.

import { useEffect, useState } from "react";
import type { LngLat } from "@/lib/producerLocationMarker";

const WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 5000,
};

export const useProducerLocation = (): LngLat | null => {
  const [location, setLocation] = useState<LngLat | null>(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    // Capture the geolocation reference so cleanup clears the same watch
    // it started, even if navigator.geolocation is later swapped out.
    const geo = navigator.geolocation;
    const watchId = geo.watchPosition(
      (position) => {
        setLocation([position.coords.longitude, position.coords.latitude]);
      },
      () => {},
      WATCH_OPTIONS,
    );

    return () => geo.clearWatch(watchId);
  }, []);

  return location;
};
